import type { MonthCell, RecurrenceOverride, RecurrenceRule, Task } from "@app/core";
import { endOfDay, endOfWeek, startOfDay, startOfWeek } from "@app/core";
import { useMemo, useRef } from "react";
import { type RuleExpansionCache, bucketMonthCells, expandTasksWindowed, sameTasks } from "./build-days.js";
import { getCalendarRuleExpansionCache } from "./calendar-expansion-cache.js";

type Rules = Record<string, RecurrenceRule>;
type Overrides = Record<string, ReadonlyArray<RecurrenceOverride>>;

/**
 * Month-view analogue of `useExpandedWindow`: builds the month grid's cells with
 * the same two incremental layers as the week/day track —
 *
 *  - the expansion is windowed + per-rule memoized (`expandTasksWindowed` with a
 *    persistent `RuleExpansionCache`), so an edit re-expands only the touched
 *    series instead of every task/series in the dataset;
 *  - a per-cell reference cache keeps an unchanged day's `MonthCell` object
 *    identity across edits, so the `React.memo`'d `MonthDayCell` for it skips
 *    re-rendering (only the day(s) an edit actually touched repaint).
 *
 * When NO cell changed, the previous cells ARRAY ref is returned too, so
 * downstream `useMemo`s (the per-week row maps) skip as well.
 *
 * `tasks` should already be view-filtered AND memoized by the caller.
 */
export function useMonthCells(
  anchor: Date,
  tasks: Task[],
  rules: Rules,
  overrides: Overrides,
  weekStartsOn: 0 | 1,
): MonthCell[] {
  const cellCache = useRef<Map<string, MonthCell>>(new Map());
  const ruleCache = useRef<RuleExpansionCache>(getCalendarRuleExpansionCache());
  const prevCells = useRef<MonthCell[] | null>(null);
  return useMemo(() => {
    const monthStart = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const monthEnd = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    const gridStart = startOfDay(startOfWeek(monthStart, { weekStartsOn }));
    const gridEnd = endOfDay(endOfWeek(monthEnd, { weekStartsOn }));
    const expanded = expandTasksWindowed(tasks, rules, overrides, gridStart, gridEnd, ruleCache.current);
    const fresh = bucketMonthCells(anchor, expanded, weekStartsOn);

    const next = new Map<string, MonthCell>();
    let allKept = prevCells.current !== null && prevCells.current.length === fresh.length;
    const out = fresh.map((cell) => {
      const iso = cell.date.toISOString();
      const prev = cellCache.current.get(iso);
      const kept = prev && prev.inMonth === cell.inMonth && sameTasks(prev.tasks, cell.tasks) ? prev : cell;
      if (kept !== prev) allKept = false;
      next.set(iso, kept);
      return kept;
    });
    cellCache.current = next; // drop entries for days outside the current grid
    const result = allKept ? prevCells.current! : out;
    prevCells.current = result;
    return result;
  }, [anchor, weekStartsOn, tasks, rules, overrides]);
}
